import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Globals } from 'app/_globals';
import { TrainingCourseForUser } from 'app/_models/training/training-course-for-user.model';
import { TrainingType } from 'app/_models/training/training-type';
import { NotificationService } from 'app/_services/notification.service';
import { SiteService } from 'app/_services/site.service';
import { TrainingService } from 'app/_services/training.service';
import {
  WorkerTrainingListViewModelService,
  WorkerTrainingRowVm,
} from 'app/training-and-inductions/worker/worker-training-list-view-model.service';
import { environment } from 'environments/environment';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import Swal from 'sweetalert2';

@Component({
  selector: 'worker-training-list',
  templateUrl: './worker-training-list.component.html',
  providers: [WorkerTrainingListViewModelService],
})
export class WorkerTrainingListComponent implements OnInit {
  @ViewChild('paginator') paginator;

  public routeRole: any;
  public trainingRows: WorkerTrainingRowVm[] = [];
  public isLoading = false;
  public isSubmitting = false;
  public pageSizes: any = environment.pageSizes;
  public pagination: any;
  public expandedTrainingIds = new Set<number>();
  public filterText = '';

  public TrainingType = TrainingType; // expose enum to template

  constructor(
    private trainingService: TrainingService,
    private notificationService: NotificationService,
    private router: Router,
    private siteService: SiteService,
    private globals: Globals,
    private workerTrainingListViewModelService: WorkerTrainingListViewModelService,
  ) {
    this.routeRole = globals.user.routeRole;
    this.resetPagination();
  }

  ngOnInit(): void {
    this.loadTrainingList();
  }

  //
  // Set up
  //

  private loadTrainingList(): void {
    this.isLoading = true;

    // Fetch the current worker course attempts
    const trainingCourses$ = this.trainingService.getCurrentWorkerTrainingCourseList();

    // Fetch sites to determine outstanding inductions and scope site lists
    const workerSites$ = this.siteService
      .getAllSitesForWorker({ size: 99999, page: 0 })
      .pipe(map((response: any) => (Array.isArray(response.body) ? response.body : [])));

    forkJoin([trainingCourses$, workerSites$]).subscribe({
      next: ([trainingCourses, workerSites]) => {
        this.trainingRows = this.workerTrainingListViewModelService.buildTrainingRows(
          trainingCourses,
          workerSites,
          this.globals.user.workerUserId,
        );
        this.pagination.totalRecords = this.filteredTrainingRows.length;
        this.isLoading = false;
      },
      error: error => {
        this.isLoading = false;
        console.error(error);
        this.notificationService.notify('danger', 'Error', 'Could not load training list data');
      },
    });
  }

  private createCourseAttempt(courseId: number, workerUserId: number): void {
    if (this.isSubmitting) {
      return;
    }

    const body = { courseId, workerUserId };
    this.isSubmitting = true;

    this.trainingService.createNewTrainingCourseForWorker(body).subscribe({
      next: result => {
        this.isSubmitting = false;
        this.router.navigate([this.routeRole, 'training', result.id]);
      },
      error: error => {
        this.isSubmitting = false;
        console.error(error);
        this.notificationService.notify('danger', 'Error', 'Could not start the course');
      },
    });
  }

  //
  // Display Getters
  //

  public get emptyCoursesMessage(): string {
    return this.trainingRows.length
      ? 'No training courses match the current filters'
      : 'No training courses available';
  }

  public get filteredTrainingRows(): WorkerTrainingRowVm[] {
    const search = this.normaliseFilterText(this.filterText);
    if (!search) {
      return this.trainingRows;
    }

    return this.trainingRows.filter(row => this.matchesTextFilter(row, search));
  }

  public get visiblePageTrainingRows(): WorkerTrainingRowVm[] {
    const start = this.pagination.page * this.pagination.size;
    return this.filteredTrainingRows.slice(start, start + this.pagination.size);
  }

  private matchesTextFilter(row: WorkerTrainingRowVm, search: string): boolean {
    const courseName = this.normaliseFilterText(row.trainingName);
    const siteOwnerName = this.normaliseFilterText(row.siteOwnerName);
    const siteNames = row.requiredSites.map(site => this.normaliseFilterText(site));
    return courseName.includes(search)
      || siteOwnerName.includes(search)
      || siteNames.some(siteName => siteName.includes(search));
  }

  private normaliseFilterText(value: string): string {
    return (value || '').trim().toLowerCase();
  }

  public isMobileExpanded(row: WorkerTrainingRowVm): boolean {
    return this.expandedTrainingIds.has(row.trainingId);
  }

  //
  // User Interaction
  //

  public async onDeleteRow(row: WorkerTrainingRowVm): Promise<void> {
    if (!row.deletableCourse) {
      return;
    }
    await this.onAbandonCourse(row.deletableCourse);
  }

  public async onAbandonCourse(course: TrainingCourseForUser): Promise<void> {
    if (this.isSubmitting) {
      return;
    }

    const result = await Swal.fire({
      title: 'Discard current progress?',
      text: 'Resets the course to previous state. Any saved progress will be lost.',
      type: 'warning',
      showCancelButton: true,
      customClass: { confirmButton: 'confirm-danger' },
      confirmButtonText: 'Yes, discard progress',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

    if (result.dismiss) {
      return;
    }

    this.isSubmitting = true;

    this.trainingService.deleteUserCourseAssigment(course.workerUserCourseId).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.notificationService.notify('success', 'Course reset', 'The course progress has been reset.');
        this.loadTrainingList();
      },
      error: error => {
        this.isSubmitting = false;
        console.error(error);
        this.notificationService.notify('danger', 'Error', 'Could not reset the course');
      },
    });
  }

  public paginate(event): void {
    this.pagination.size = event.rows;
    this.pagination.page = event.page;
  }

  public resetPagination(): void {
    this.pagination = {
      size: this.pageSizes[0],
      page: 0,
      totalRecords: 0,
    };
  }

  public onFilterTextChanged(filterText: string): void {
    this.filterText = filterText || '';
    this.goToFirstPage();
    this.pagination.totalRecords = this.filteredTrainingRows.length;
  }

  public toggleMobileExpansion(row: WorkerTrainingRowVm): void {
    if (this.expandedTrainingIds.has(row.trainingId)) {
      this.expandedTrainingIds.delete(row.trainingId);
      return;
    }

    this.expandedTrainingIds.add(row.trainingId);
  }

  public onRowAction(row: WorkerTrainingRowVm): void {
    if (this.isSubmitting || !row.action) {
      return;
    }

    if (row.action.kind === 'navigate') {
      this.router.navigate(['/', this.routeRole, 'training', row.action.userCourseId]);
      return;
    }

    this.createCourseAttempt(row.action.courseId, row.action.workerUserId);
  }

  private goToFirstPage(): void {
    this.pagination.page = 0;
    if (this.paginator) {
      this.paginator.first = 0;
    }
  }
}
